/**
 * 依頼者追加可能書類 whitelist (19-D-F1) の admin UI 純ロジック
 *
 * React / API に依存しない pure helper。並べ替え・差分判定・client 側重複チェック・
 * PUT payload 構築を担い、DocumentWhitelistSection から利用する。BE の検証
 * (`planRequesterAddableUpdate`) を FE 側で完全再現はしないが、送信前の UX ガードとして
 * name 重複 (trim + 大文字小文字無視) を BE と同じ規則で先出しする。
 */
import type { RequesterAddableAdminOption } from '@/types';
import { MAX_REQUESTER_ADDABLE_OPTIONS } from '@/constants/requesterAddableDocuments';

/** 追加上限に達しているか (無効 option も件数に含む) */
export function whitelistAtMax(count: number): boolean {
  return count >= MAX_REQUESTER_ADDABLE_OPTIONS;
}

/**
 * whitelist 保存 API のエラーを status に分類する (pure)。
 * apiFetch は 4xx で `ApiResponseError`(code 付き) を throw する。BE の 409 は code='CONFLICT'
 * (version 楽観ロック / 並行更新)、403 は code='FORBIDDEN'。ここでは code を duck-type で見て、
 * 呼び出し側 (useUpdateRequesterAddableDocuments) の専用ハンドリングに橋渡しする。
 */
export function classifyWhitelistSaveError(err: unknown): 'conflict' | 'forbidden' | 'error' {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  if (code === 'CONFLICT') return 'conflict';
  if (code === 'FORBIDDEN') return 'forbidden';
  return 'error';
}

/**
 * admin UI のローカル編集 state 上の 1 option。
 * optionId が null の要素は「新規 (未保存)」で、保存時は optionId を送らず server が発行する。
 */
export interface LocalWhitelistOption {
  /** null = 新規 (server が UUID を発行)。既存は server 由来の optionId */
  optionId: string | null;
  name: string;
  category: string;
  repeatable: boolean;
  enabled: boolean;
  description: string | null;
  sortOrder: number;
}

/** server option をローカル編集用に写す (sortOrder 昇順) */
export function fromServerOptions(options: RequesterAddableAdminOption[]): LocalWhitelistOption[] {
  return [...options]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((o) => ({
      optionId: o.optionId,
      name: o.name,
      category: o.category,
      repeatable: o.repeatable,
      enabled: o.enabled,
      description: o.description ?? null,
      sortOrder: o.sortOrder,
    }));
}

/** sortOrder を配列順 (0..N-1) に再採番する (BE の正規化と同じ 0 始まり) */
export function reindexSortOrder(list: LocalWhitelistOption[]): LocalWhitelistOption[] {
  return list.map((o, i) => ({ ...o, sortOrder: i }));
}

/** name の正規化 key (BE dup_name と同じ: trim + 大文字小文字無視) */
export function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * client 側の書類名重複チェック (送信前 UX ガード)。
 * 無効 option も含めて判定する (BE と同じ)。重複があれば最初に衝突した原文名を返す。
 */
export function findDuplicateName(options: Array<{ name: string }>): string | null {
  const seen = new Set<string>();
  for (const o of options) {
    const key = normalizeName(o.name);
    if (!key) continue; // 空名は name 必須バリデーションで別途弾く
    if (seen.has(key)) return o.name.trim();
    seen.add(key);
  }
  return null;
}

/**
 * サーバー値とローカル値に差分があるか (未保存判定)。
 * - 件数が違う / 新規 option (optionId=null) が含まれる → 差分あり
 * - 位置ごとに optionId / name / category / repeatable / enabled / description が違う → 差分あり
 */
export function hasWhitelistChanges(
  server: RequesterAddableAdminOption[],
  local: LocalWhitelistOption[],
): boolean {
  if (server.length !== local.length) return true;
  const sortedServer = [...server].sort((a, b) => a.sortOrder - b.sortOrder);
  for (let i = 0; i < sortedServer.length; i++) {
    const s = sortedServer[i];
    const l = local[i];
    if (l.optionId === null) return true; // 新規
    if (
      s.optionId !== l.optionId ||
      s.name !== l.name ||
      s.category !== l.category ||
      s.repeatable !== l.repeatable ||
      s.enabled !== l.enabled ||
      (s.description ?? null) !== (l.description ?? null)
    ) {
      return true;
    }
  }
  return false;
}

/** PUT リクエストの 1 option (新規は optionId を含めない = server 生成) */
export interface WhitelistPutOption {
  optionId?: string;
  name: string;
  category: string;
  repeatable: boolean;
  enabled: boolean;
  description?: string;
}

/** PUT リクエスト body */
export interface WhitelistPutPayload {
  expectedVersion: number;
  options: WhitelistPutOption[];
}

/**
 * PUT payload を構築する。**配列順を正本**として送り (BE が sortOrder を正規化)、
 * 新規 option は optionId を送らない (server 生成)。空 description は送らない。
 */
export function buildWhitelistPutPayload(
  version: number,
  local: LocalWhitelistOption[],
): WhitelistPutPayload {
  return {
    expectedVersion: version,
    options: local.map((o) => {
      const out: WhitelistPutOption = {
        name: o.name.trim(),
        category: o.category,
        repeatable: o.repeatable,
        enabled: o.enabled,
      };
      if (o.optionId) out.optionId = o.optionId;
      const desc = o.description?.trim();
      if (desc) out.description = desc;
      return out;
    }),
  };
}
